import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  QuestionMarkCircleIcon, 
  BookOpenIcon, 
  VideoCameraIcon,
  ChatBubbleLeftRightIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  PlayIcon,
  DocumentTextIcon,
  LightBulbIcon
} from '@heroicons/react/24/outline';
import AppHeader from '../components/AppHeader';
import AppFooter from '../components/AppFooter';

const HelpSupportPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);

  const faqs = [
    {
      question: 'How do I create a new team?',
      answer: 'To create a new team, click the "Create New Team" button on your dashboard or teams page. Fill in the team name, description, and invite members by email. You can assign different roles (admin, editor, viewer) to team members.'
    },
    {
      question: 'Can I collaborate with people outside my organization?',
      answer: 'Yes! StyleSync allows you to invite anyone with an email address to collaborate on your projects. Simply send them an invitation, and they can join your team once they create an account.'
    },
    {
      question: 'How do I share my mood board with clients?',
      answer: 'You can share your mood board by generating a shareable link or inviting specific people via email. Go to your mood board, click the share button, and choose your preferred sharing method.'
    },
    {
      question: 'What file formats are supported for uploads?',
      answer: 'StyleSync supports most common image formats including JPG, PNG, GIF, SVG, and WebP. For documents, we support PDF, DOC, DOCX, and TXT files. Maximum file size is 10MB per file.'
    },
    {
      question: 'How do I export my designs?',
      answer: 'To export your designs, select the items you want to export, click the export button, and choose your preferred format (PNG, JPG, PDF, or SVG). You can also export entire boards as a single file.'
    },
    {
      question: 'Is my data secure?',
      answer: 'Yes, we take security seriously. All data is encrypted in transit and at rest. We use industry-standard security practices and regularly conduct security audits. Your data is backed up regularly and stored in secure data centers.'
    }
  ];

  const tutorials = [
    {
      title: 'Getting Started with StyleSync',
      duration: '5 min',
      description: 'Learn the basics of creating your first project and inviting team members.',
      icon: PlayIcon,
      category: 'Beginner'
    },
    {
      title: 'Creating Mood Boards',
      duration: '8 min',
      description: 'Master the art of creating beautiful and organized mood boards.',
      icon: VideoCameraIcon,
      category: 'Intermediate'
    },
    {
      title: 'Team Collaboration Features',
      duration: '12 min',
      description: 'Discover all the ways your team can work together effectively.',
      icon: ChatBubbleLeftRightIcon,
      category: 'Advanced'
    },
    {
      title: 'Advanced Design Tools',
      duration: '15 min',
      description: 'Explore advanced features like layers, effects, and custom templates.',
      icon: LightBulbIcon,
      category: 'Advanced'
    }
  ];

  const resources = [
    {
      title: 'User Guide',
      description: 'Comprehensive guide covering all features and functionality.',
      icon: BookOpenIcon,
      link: '#'
    },
    {
      title: 'API Documentation',
      description: 'Technical documentation for developers and integrations.',
      icon: DocumentTextIcon,
      link: '#'
    },
    {
      title: 'Template Library',
      description: 'Browse and download professional templates for your projects.',
      icon: DocumentTextIcon,
      link: '#'
    }
  ];

  const filteredFaqs = faqs.filter(faq => 
    faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
    faq.answer.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center">
              <QuestionMarkCircleIcon className="w-8 h-8 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Help & Support</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Find answers to your questions, learn new features, and get the most out of StyleSync.
          </p>
        </motion.div>

        {/* Search Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="max-w-2xl mx-auto mb-12"
        >
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search for help articles, tutorials, or FAQs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
            />
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* FAQ Section */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="lg:col-span-2"
          >
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">Frequently Asked Questions</h2>
            <div className="space-y-4">
              {filteredFaqs.map((faq, index) => (
                <div key={index} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  <button
                    onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                    className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                  >
                    <span className="font-medium text-gray-900">{faq.question}</span>
                    {expandedFaq === index ? (
                      <ChevronUpIcon className="w-5 h-5 text-gray-500" />
                    ) : (
                      <ChevronDownIcon className="w-5 h-5 text-gray-500" />
                    )}
                  </button>
                  {expandedFaq === index && (
                    <div className="px-6 pb-4">
                      <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </motion.div>

          {/* Sidebar */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="space-y-8"
          >
            {/* Quick Contact */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Still need help?</h3>
              <p className="text-gray-600 mb-4">
                Can't find what you're looking for? Our support team is here to help.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-200"
              >
                <ChatBubbleLeftRightIcon className="w-4 h-4 mr-2" />
                Contact Support
              </Link>
            </div>

            {/* Tutorials */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Video Tutorials</h3>
              <div className="space-y-4">
                {tutorials.map((tutorial, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                      <tutorial.icon className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-medium text-gray-900">{tutorial.title}</h4>
                      <p className="text-xs text-gray-500 mt-1">{tutorial.duration} • {tutorial.category}</p>
                      <p className="text-xs text-gray-600 mt-1">{tutorial.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Resources */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Resources</h3>
              <div className="space-y-3">
                {resources.map((resource, index) => (
                  <a
                    key={index}
                    href={resource.link}
                    className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <resource.icon className="w-5 h-5 text-blue-600" />
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">{resource.title}</h4>
                      <p className="text-xs text-gray-500">{resource.description}</p>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </motion.div>
        </div>

        {/* Quick Tips Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mt-12 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl border border-blue-200 p-8"
        >
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Quick Tips</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="flex items-start space-x-3">
              <LightBulbIcon className="w-6 h-6 text-yellow-500 mt-1" />
              <div>
                <h3 className="font-medium text-gray-900">Keyboard Shortcuts</h3>
                <p className="text-sm text-gray-600">Use Ctrl/Cmd + K to quickly search across your projects.</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <LightBulbIcon className="w-6 h-6 text-yellow-500 mt-1" />
              <div>
                <h3 className="font-medium text-gray-900">Auto-save</h3>
                <p className="text-sm text-gray-600">All your work is automatically saved as you create and edit.</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <LightBulbIcon className="w-6 h-6 text-yellow-500 mt-1" />
              <div>
                <h3 className="font-medium text-gray-900">Version History</h3>
                <p className="text-sm text-gray-600">Access previous versions of your designs anytime.</p>
              </div>
            </div>
          </div>
        </motion.div>
      </main>

      <AppFooter />
    </div>
  );
};

export default HelpSupportPage; 